/**
 * useNotificationToasts — always-on toast zamanlayıcı (N2-03, D-T2)
 *
 * Bildirim listesini periyodik (45s) çeker; `!is_read && !hasSeen(id)` olan her
 * bildirim için sonner toast atar ve id'yi cihaz-bazlı seen deposuna yazar →
 * bir daha toast atmaz (tekrar bombardımanı yok). Online olunca (refetchOnReconnect
 * + focus) daha önce toast görülmemiş okunmamışlar 5 saat öncesi bile olsa İLK KEZ
 * gösterilir; canlı gelen her yeni bildirim de toast'lanır. BACKEND DEĞİŞMEZ.
 *
 * Flood-control ($10K madde 6): en yeni 3 bildirim ayrı toast; fazlası "+N yeni
 * bildirim" özet toast. Tümü (gösterilen + özete giren) seen işaretlenir → tek yazma.
 * Gösterilen her bildirim için 'sensei:notification:fly' event yayar; uçan animasyon
 * overlay (notification-toaster) bunu dinler (ayrışım: hook karar+toast, overlay uçuş).
 *
 * XSS (T-N2-05): title/body JSX text node olarak render (otomatik escape);
 * dangerouslySetInnerHTML KULLANILMAZ. Test edilebilirlik için toast 'sonner'dan
 * import edilir → vi.mock ile mock'lanabilir.
 *
 * N4 (kullanıcı, 2026-07-07): "zile uçan pill" animasyonu kaldırıldı; hook artık
 * yalnız normal sonner toast atar.
 */
import { useEffect, createElement } from 'react'
import { toast } from 'sonner'
import { Bell } from 'lucide-react'

import { NOTIFICATION_POLL_MS, useNotificationsList } from '@/hooks/use-notifications'
import { addManySeen, hasSeen } from '@/lib/notification-seen-store'
import type { NotificationItem } from '@/services/notifications-api'

/** Aynı anda ayrı toast olarak gösterilecek en fazla bildirim (flood-control). */
const MAX_TOASTS = 3

/** Tek bir bildirim için sonner özel içeriği — Bell + başlık + gövde (token-only). */
function renderToastContent(n: NotificationItem) {
  return createElement(
    'div',
    {
      className: 'flex items-start gap-2.5 cursor-pointer',
      onClick: () => {
        window.dispatchEvent(new CustomEvent('sensei:notification:open'))
        toast.dismiss(`notif-${n.id}`)
      },
    },
    createElement(Bell, {
      className: 'mt-0.5 size-4 shrink-0 text-brand-chartreuse',
      'aria-hidden': true,
    }),
    createElement(
      'div',
      { className: 'flex min-w-0 flex-col gap-0.5' },
      createElement(
        'span',
        { className: 'line-clamp-1 text-sm font-semibold text-foreground' },
        n.title
      ),
      createElement(
        'span',
        { className: 'line-clamp-2 text-xs text-muted-foreground' },
        n.body
      )
    )
  )
}

/**
 * Always-on toast hook. app-shell içinde tek mount edilir (authenticated layout).
 * Görsel çıktı üretmez — yalnız yan etki (toast + fly event).
 */
export function useNotificationToasts(): void {
  const { data } = useNotificationsList({ enabled: true, refetchInterval: NOTIFICATION_POLL_MS })

  useEffect(() => {
    if (!data || data.length === 0) return

    // Cihazda daha önce toast görülmemiş okunmamışlar
    const candidates = data
      .filter((n) => !n.is_read && !hasSeen(n.id))
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )

    if (candidates.length === 0) return

    const shown = candidates.slice(0, MAX_TOASTS)
    for (const n of shown) {
      toast.custom(() => renderToastContent(n), {
        id: `notif-${n.id}`,
        duration: 4500,
      })
    }

    if (candidates.length > MAX_TOASTS) {
      toast(`+${candidates.length - MAX_TOASTS} yeni bildirim`)
    }

    // Gösterilen + özete girenlerin TÜMÜ seen → tek yazma, tekrar toast atmaz
    addManySeen(candidates.map((n) => n.id))
  }, [data])
}
